import { ActionFilter } from "./matchesCoordinatorFilter";

const STORAGE_KEY = "logisticsPlatform.filters";

export type LogisticsPickupsView = "table" | "pipeline" | "calendar";

export type LogisticsPlatformFilters = {
  view: LogisticsPickupsView;
  search: string;
  coordinatorFilter: string;
  vendorFilter: string;
  actionFilter: ActionFilter;
};

const DEFAULT_FILTERS: LogisticsPlatformFilters = {
  view: "pipeline",
  search: "",
  coordinatorFilter: "all",
  vendorFilter: "all",
  actionFilter: "all",
};

const VIEWS: LogisticsPickupsView[] = ["table", "pipeline", "calendar"];

function isView(value: unknown): value is LogisticsPickupsView {
  return typeof value === "string" && VIEWS.includes(value as LogisticsPickupsView);
}

function isActionFilter(value: unknown): value is ActionFilter {
  return value === "all" || value === "needs_action";
}

function readStoredFilters(): LogisticsPlatformFilters | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<LogisticsPlatformFilters>;
    if (!parsed || typeof parsed !== "object") return null;

    return {
      view: isView(parsed.view) ? parsed.view : DEFAULT_FILTERS.view,
      search: typeof parsed.search === "string" ? parsed.search : DEFAULT_FILTERS.search,
      coordinatorFilter:
        typeof parsed.coordinatorFilter === "string" ? parsed.coordinatorFilter : DEFAULT_FILTERS.coordinatorFilter,
      vendorFilter: typeof parsed.vendorFilter === "string" ? parsed.vendorFilter : DEFAULT_FILTERS.vendorFilter,
      actionFilter: isActionFilter(parsed.actionFilter) ? parsed.actionFilter : DEFAULT_FILTERS.actionFilter,
    };
  } catch {
    return null;
  }
}

export function readLogisticsPlatformFilters(searchParams: URLSearchParams): LogisticsPlatformFilters {
  const stored = readStoredFilters();
  if (stored) return stored;

  const view = searchParams.get("view");
  const action = searchParams.get("action");
  const coordinator = searchParams.get("coordinator");
  const vendor = searchParams.get("vendor");

  return {
    view: isView(view) ? view : DEFAULT_FILTERS.view,
    search: searchParams.get("q") ?? DEFAULT_FILTERS.search,
    coordinatorFilter: coordinator || DEFAULT_FILTERS.coordinatorFilter,
    vendorFilter: vendor || DEFAULT_FILTERS.vendorFilter,
    actionFilter: isActionFilter(action) ? action : DEFAULT_FILTERS.actionFilter,
  };
}

export function writeLogisticsPlatformFilters(filters: LogisticsPlatformFilters) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
}

export function logisticsPlatformFiltersToSearchParams(current: URLSearchParams, filters: LogisticsPlatformFilters) {
  const next = new URLSearchParams(current);

  if (filters.view === DEFAULT_FILTERS.view) next.delete("view");
  else next.set("view", filters.view);

  if (filters.search) next.set("q", filters.search);
  else next.delete("q");

  if (filters.coordinatorFilter === DEFAULT_FILTERS.coordinatorFilter) next.delete("coordinator");
  else next.set("coordinator", filters.coordinatorFilter);

  if (filters.vendorFilter === DEFAULT_FILTERS.vendorFilter) next.delete("vendor");
  else next.set("vendor", filters.vendorFilter);

  if (filters.actionFilter === DEFAULT_FILTERS.actionFilter) next.delete("action");
  else next.set("action", filters.actionFilter);

  return next;
}
